import { createRouter, createWebHashHistory } from "vue-router";
import { nextTick } from "vue";

// 保存每个路由的滚动位置
const scrollPositions = new Map<string, number>();

// 实时保存当前路由的滚动位置
let currentRouteName: string | symbol | null | undefined = null;
let scrollHandler: (() => void) | null = null;
let scrollTimer: ReturnType<typeof setTimeout> | null = null;

const getQueryStringValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    const firstItem = value[0];
    return typeof firstItem === 'string' ? firstItem : undefined;
  }
  return undefined;
};

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    // 不在这里保存滚动位置，因为已经在 beforeEach 和实时监听中保存了
    // 直接使用已保存的位置

    // 优先使用手动保存的目标路由的滚动位置
    if (to.name && scrollPositions.has(to.name as string)) {
      const savedScrollTop = scrollPositions.get(to.name as string);
      // 使用 Promise 确保在 DOM 更新后滚动
      return new Promise((resolve) => {
        nextTick(() => {
          // 等待 DOM 更新和可能的异步内容加载
          setTimeout(() => {
            resolve({
              left: 0,
              top: savedScrollTop || 0
            });
          }, 150);
        });
      });
    }

    // 如果没有手动保存的位置，使用浏览器保存的滚动位置
    if (savedPosition) {
      return savedPosition;
    }

    // 否则返回到顶部
    return { left: 0, top: 0 };
  },
  routes: [
    {
      path: "/",
      name: "page",
      meta: { show: true },
      component: () => import("../components/PageIndex/index.vue"),
    },
    {
      path: "/homeIndex",
      name: "home",
      meta: { show: true },
      component: () => import("../components/Liuyao/HomeIndex/index.vue"),
    },
    {
      path: "/question",
      name: "question",
      meta: { show: true },
      component: () => import("../components/Liuyao/Question/index.vue"),
    },
    {
      path: "/shake",
      name: "shake",
      meta: { show: true },
      component: () => import("../components/Liuyao/Shake/index.vue"),
    },
    {
      path: "/handShake",
      name: "handShake",
      meta: { show: true },
      component: () => import("../components/Liuyao/HandShake/index.vue"),
    },
    {
      path: "/fixShake",
      name: "fixShake",
      meta: { show: true },
      component: () => import("../components/Liuyao/FixShake/index.vue"),
    },
    {
      path: "/answer",
      name: "answer",
      meta: { show: true },
      component: () => import("../components/Liuyao/Answer/index.vue"),
    },
    {
      path: "/mine",
      name: "mine",
      meta: { show: true },
      component: () => import("../components/Mine/index.vue"),
    },
    {
      path: "/vip-rights",
      name: "vipRights",
      meta: { show: true },
      component: () => import("../views/VipRights.vue"),
    },
    {
      path: "/history",
      name: "history",
      meta: { show: true },
      component: () => import("../components/History/index.vue"),
    },
    {
      path: "/order",
      name: "order",
      meta: { show: true },
      component: () => import("../components/Order/index.vue"),
    },
    {
      path: "/integral",
      name: "integral",
      meta: { show: true },
      component: () => import("../components/Liuyao/Integral/index.vue"),
    },
    {
      path: "/shareResult",
      name: "shareResult",
      meta: { show: true },
      component: () => import("../components/Liuyao/ShareResult/index.vue"),
    },
    {
      path: "/settings",
      name: "settings",
      meta: { show: true },
      component: () => import("../components/Settings/index.vue"),
    },
    {
      path: "/archiveList/:id?",
      name: "archiveList",
      meta: { show: false },
      component: () => import("../components/ArchiveList/index.vue"),
    },
    {
      path: "/archiveAdd",
      name: "archiveAdd",
      meta: { show: false },
      component: () => import("../components/ArchiveAdd/index.vue"),
    },
    {
      path: "/archives/addArchivesByShare",
      name: "addArchivesByShare",
      meta: { show: true },
      component: () => import("../components/ArchiveAdd/AddByShare/index.vue"),
    },
    {
      path: "/huangLi",
      name: "huangLi",
      meta: { show: true },
      component: () => import("../components/HuangLi/index.vue"),
    },
    {
      path: "/fateInfo/:id",
      name: "fateInfo",
      meta: { show: false },
      component: () => import("../components/FateInfo/index.vue"),
    },
    {
      path: "/mbtiAnalysisHomeIndex",
      name: "mbtiAnalysisHomeIndex",
      meta: { show: true },
      component: () => import("../components/MbtiAnalysis/HomeIndex/index.vue"),
    },
    {
      path: "/mbtiAnalysisQuestion",
      name: "mbtiAnalysisQuestion",
      meta: { show: true },
      component: () => import("../components/MbtiAnalysis/Question/index.vue"),
    },

    {
      path: "/mbtiAnalysisAnswer",
      name: "mbtiAnalysisAnswer",
      meta: { show: true },
      component: () => import("../components/MbtiAnalysis/Answer/index.vue"),
    },

    {
      path: "/mbtiAnalysisShareResult",
      name: "mbtiAnalysisShareResult",
      meta: { show: true },
      component: () =>
        import("../components/MbtiAnalysis/ShareResult/index.vue"),
    },
    {
      path: "/mbtiSocialHomeIndex",
      name: "mbtiSocialHomeIndex",
      meta: { show: true },
      component: () => import("../components/MbtiSocial/HomeIndex/index.vue"),
    },
    {
      path: "/mbtiSocialQuestion",
      name: "mbtiSocialQuestion",
      meta: { show: true },
      component: () => import("../components/MbtiSocial/Question/index.vue"),
    },

    {
      path: "/mbtiSocialAnswer",
      name: "mbtiSocialAnswer",
      meta: { show: true },
      component: () => import("../components/MbtiSocial/Answer/index.vue"),
    },

    {
      path: "/mbtiSocialShareResult",
      name: "mbtiSocialShareResult",
      meta: { show: true },
      component: () => import("../components/MbtiSocial/ShareResult/index.vue"),
    },
    {
      path: "/baziHomeIndex",
      name: "baziHomeIndex",
      meta: { show: true },
      component: () => import("../components/BaZi/HomeIndex/index.vue"),
    },
    {
      path: "/baziReport",
      name: "baziReport",
      meta: { show: true },
      component: () => import("../components/BaZi/BaZiReport/index.vue"),
    },
    {
      path: "/baziReportContent",
      name: "baziReportContent",
      meta: { show: true },
      component: () => import("../components/BaZi/BaZiReportContent/index.vue"),
    },
    {
      path: "/bazireportContentLoading",
      name: "bazireportContentLoading",
      meta: { show: true },
      component: () => import("../components/BaZi/BaZiReportContentLoading/index.vue"),
    },
    {
      path: "/chat",
      name: "chat",
      meta: { show: true },
      component: () => import("../components/Chat/index.vue"),
    },
    {
      path: "/chatHistory",
      name: "chatHistory",
      meta: { show: true },
      component: () => import("../views/chat/History.vue"),
    },
    {
      path: "/lovePhotoHomeIndex",
      name: "lovePhotoHomeIndex",
      meta: { show: true },
      component: () => import("../components/LovePhoto/HomeIndex/index.vue"),
    },
    {
      path: '/lovePhoto/lovePhotoContent',
      name: 'LovePhotoContent',
      component: () => import('../components/LovePhoto/LovePhotoContent/index.vue'),
      meta: {
        title: '桃花画像生成',
        show: true
      }
    },
    {
      path: '/lovePhoto/result',
      name: 'LovePhotoResult',
      component: () => import('../components/LovePhoto/Result/index.vue'),
      meta: {
        title: '桃花画像结果',
        show: true
      }
    },
    {
      path: '/lovePhoto/share',
      name: 'LovePhotoShare',
      component: () => import('../components/LovePhoto/Share/index.vue'),
      meta: {
        title: '桃花画像分享',
        show: true
      }
    },
    {
      path: "/baziHepanMain",
      name: "baziHepanMain",
      meta: { show: true },
      component: () => import("../components/BaziHepan/BaziHepanMain/index.vue"),
    },
    {
      path: "/baziHepanReport",
      name: "baziHepanReport",
      meta: { show: true },
      component: () => import("../components/BaziHepan/BaziHepanReport/index.vue"),
    },
    {
      path: "/hepan/share",
      name: "hepanShare",
      meta: { show: true },
      component: () => import("../components/BaziHepan/BaziHepanReportShare/index.vue"),
    },
    {
      path: "/hepan/simpleShare",
      name: "hepanSimpleShare",
      meta: { show: true },
      component: () => import("../components/BaziHepan/BaziHepanMainShare/index.vue"),
    },
    {
      path: "/qiMenDunJiaHomeIndex",
      name: "qiMenDunJiaHomeIndex",
      meta: { show: true },
      component: () => import("../components/QiMenDunJia/HomeIndex/index.vue"),
    },
    {
      path: "/qiMenDunJiaQuestion",
      name: "qiMenDunJiaQuestion",
      meta: { show: true },
      component: () => import("../components/QiMenDunJia/Question/index.vue"),
    },
    {
      path: "/qiMenDunJiaAnswer",
      name: "qiMenDunJiaAnswer",
      meta: { show: true },
      component: () => import("../components/QiMenDunJia/Answer/index.vue"),
    },
    {
      path: "/qimen/share",
      name: "qiMenDunJiaShare",
      meta: { show: true },
      component: () => import("../components/QiMenDunJia/Share/index.vue"),
    },
    {
      path: "/psychologyTest",
      name: "psychologyTest",
      meta: { show: true },
      component: () => import("../components/PsychologyTest/HomeIndex/index.vue"),
    },
    {
      path: "/psychologyTest/detail",
      name: "psychologyTestDetail",
      meta: { show: true },
      component: () => import("../components/PsychologyTest/ScaleDetail/index.vue"),
    },
    {
      path: "/psychologyTest/question",
      name: "psychologyTestQuestion",
      meta: { show: true },
      component: () => import("../components/PsychologyTest/TestQuestion/index.vue"),
    },
    {
      path: "/psychologyTest/result",
      name: "psychologyTestResult",
      meta: { show: true },
      component: () => import("../components/PsychologyTest/TestResult/index.vue"),
    },
    {
      path: "/scaleReport",
      name: "scaleReport",
      meta: { show: true },
      component: () => import("../components/PsychologyTest/ScaleReport/index.vue"),
    },
    {
      path: "/psychologyTestHistory",
      name: "psychologyTestHistory",
      meta: { show: true },
      component: () => import("../components/PsychologyTestHistory/index.vue"),
    },
    {
      path: "/psychologyTest/shareResult",
      name: "shareTestResult",
      meta: { show: true },
      component: () => import("../components/PsychologyTest/ShareTestResult/index.vue"),
    },
    {
      path: "/Scale/RMP",
      name: "RMP",
      meta: { show: true },
      component: () => import("../components/SingleScale/RMP/ScaleDetail/index.vue"),
    },
    {
      path: "/Scale/RMP/question",
      name: "RMPQuestion",
      meta: { show: true },
      component: () => import("../components/SingleScale/RMP/TestQuestion/index.vue"),
    },
    {
      path: "/Scale/RMP/result",
      name: "RMPResult",
      meta: { show: true },
      component: () => import("../components/SingleScale/RMP/TestResult/index.vue"),
    },
    {
      path: "/Scale/RMP/report",
      name: "RMPReport",
      meta: { show: true },
      component: () => import("../components/SingleScale/RMP/ScaleReport/index.vue"),
    },
    {
      path: "/myFeedback",
      name: "myFeedback",
      meta: { show: true },
      component: () => import("../components/MyFeedback/index.vue"),
    },
    {
      path: "/cashier-example",
      name: "cashierExample",
      meta: { show: true },
      component: () => import("../views/CashierExample.vue"),
    },
    {
      path: "/payResult",
      name: "payResult",
      meta: { show: true },
      component: () => import("../views/PayResult.vue"),
    },
    {
      path: "/payQuit",
      name: "payQuit",
      meta: { show: true },
      component: () => import("../views/PayQuit.vue"),
    }
  ],
});

// 设置滚动监听器来实时保存滚动位置（使用节流优化性能）
const setupScrollListener = () => {
  if (scrollHandler) {
    window.removeEventListener('scroll', scrollHandler, true);
  }
  
  scrollHandler = () => {
    // 清除之前的定时器
    if (scrollTimer) {
      clearTimeout(scrollTimer);
    }
    
    // 使用节流，延迟保存滚动位置
    scrollTimer = setTimeout(() => {
      if (currentRouteName && typeof currentRouteName === 'string') {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        scrollPositions.set(currentRouteName as string, scrollTop);
      }
    }, 100); // 100ms 节流
  };
  
  window.addEventListener('scroll', scrollHandler, true);
};

// 在路由切换前保存当前页面的滚动位置，并设置新的滚动监听器
router.beforeEach((to, from) => {
  if (typeof window !== 'undefined') {
    const userFromValue = getQueryStringValue(to?.query?.userFrom);
    if (userFromValue && userFromValue.trim()) {
      window.localStorage.setItem('userFrom', userFromValue.trim());
    }

    const channelValue = getQueryStringValue(to?.query?.channel);
    if (channelValue && channelValue.trim()) {
      window.sessionStorage.setItem('channel', channelValue.trim());
    }
  }

  // 先强制保存当前路由的最后一次滚动位置（清除节流，立即保存）
  if (currentRouteName && typeof currentRouteName === 'string' && currentRouteName === from.name) {
    if (scrollTimer) {
      clearTimeout(scrollTimer);
      scrollTimer = null;
    }
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    scrollPositions.set(currentRouteName, scrollTop);
  }

  // 保存当前页面的滚动位置（双重保险）
  if (from.name && from.name !== to.name) {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    scrollPositions.set(from.name as string, scrollTop);
  }
  
  // 更新当前路由名称
  currentRouteName = to.name;
});

// 在路由切换后设置滚动监听器
router.afterEach(() => {
  setupScrollListener();
});

// 初始化滚动监听器
setupScrollListener();

export default router;
